import type { SortableColumnsConfig } from '@/components/table/sortableColumns/types';
import type { Column, Columns } from '@/components/table/types';
import type { Agent } from '@/hooks/useAgents';
import { useSmartColumnPositioning } from '@/hooks/useSmartColumnPositioning';

/**
 * Hook parameters matching useSortableColumn interface
 */
interface UseAgentColumnsProps {
  /** Storage key for persisting column state */
  key: string;
  /** Default column definitions */
  defaultColumns: Column<Agent>[];
  /** Sortable column configuration */
  defaultConfig: SortableColumnsConfig;
}

/**
 * Return type for useAgentColumns hook
 */
export interface UseAgentColumnsReturn {
  /** Rendered table columns with current configuration */
  columns: Columns<Agent>;
  /** Currently active column IDs in display order */
  activeColumns: string[] | undefined;
  /** Function to update active columns with smart positioning */
  setActiveColumns: (newColumns: string[]) => void;
}

/**
 * Custom hook for managing agent table column configuration with smart positioning
 *
 * Uses useSmartColumnPositioning for intelligent column positioning:
 * - Status column returns to first position when toggled on
 * - Last seen date column returns to last position when toggled on
 * - User drag-to-reorder preferences are preserved
 *
 * This pattern matches the asset, vulnerability, and seed table behavior.
 *
 * @param props - Hook configuration
 * @returns Column configuration state and controls with smart positioning
 *
 * @example
 * ```tsx
 * const { columns, activeColumns, setActiveColumns } = useAgentColumns({
 *   key: 'agents',
 *   defaultColumns,
 *   defaultConfig: sortableColumnsConfig,
 * });
 *
 * <Table
 *   columns={columns}
 *   sortableColumns={{
 *     activeColumns,
 *     setActiveColumns,
 *     defaultConfig: sortableColumnsConfig,
 *   }}
 * />
 * ```
 */
export const useAgentColumns = ({
  key,
  defaultColumns,
  defaultConfig,
}: UseAgentColumnsProps): UseAgentColumnsReturn => {
  // Apply smart positioning with rules
  const { activeColumns, setActiveColumns, columns } =
    useSmartColumnPositioning({
      key,
      defaultColumns,
      defaultConfig,
      positioningRules: {
        firstColumns: ['status'], // Status → first when toggled on
        lastColumns: ['last_seen_at'], // Last seen date → last when toggled on
      },
    });

  return {
    columns,
    activeColumns,
    setActiveColumns,
  };
};
